/**
 * ESRS E4 — Biodiversity & Ecosystems.
 *
 * Site register + per-site biodiversity assessment (GLOBIO Mean Species
 * Abundance factors, protected-area proximity) surfacing the ESRS E4-5
 * impact metrics. Follows the EsgKit / CBAMModule house idiom: DESIGN.md
 * tokens as inline styles, JetBrains Mono for every data value, status as
 * color + icon + label. Backend: backend/routes/esrs_e4_routes.py.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TreePine, MapPin, Layers, Sprout, Plus, ChevronDown, ChevronRight,
  Gauge, ShieldAlert, ShieldCheck, ScanLine, Recycle,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C, TONES,
  DataQualityBadge, MSA_SOURCE,
} from "../../../components/esg/EsgKit";
import { useEntities } from "../../../hooks/useEntities";

// Backend ImpactSeverity enum → Badge tone + icon.
const SEVERITY = {
  critical: { tone: TONES.red, Icon: ShieldAlert, label: "Critical" },
  high: { tone: TONES.red, Icon: ShieldAlert, label: "High" },
  moderate: { tone: TONES.amber, Icon: Gauge, label: "Moderate" },
  low: { tone: TONES.emerald, Icon: ShieldCheck, label: "Low" },
  negligible: { tone: TONES.emerald, Icon: ShieldCheck, label: "Negligible" },
};
const severityBadge = (s) => {
  const m = SEVERITY[String(s || "").toLowerCase()];
  return m ? <Badge c={m.tone} Icon={m.Icon} label={m.label} /> : <Badge c={TONES.zinc} label="—" />;
};

// Backend LandUseType enum (svc.LandUseType) — value + human label.
const LAND_USE = [
  { v: "natural_forest", l: "Natural forest" },
  { v: "plantation", l: "Plantation" },
  { v: "agricultural", l: "Agricultural" },
  { v: "grassland", l: "Grassland" },
  { v: "wetland", l: "Wetland" },
  { v: "urban", l: "Urban" },
  { v: "industrial", l: "Industrial" },
  { v: "mining", l: "Mining" },
  { v: "water_body", l: "Water body" },
];
const landUseLabel = (v) => LAND_USE.find((o) => o.v === v)?.l || v || "—";

const gap = (n = 16) => ({ display: "grid", gap: n });

// ── Register-site form ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
  site_id: "", site_name: "", latitude: "", longitude: "", country: "",
  area_hectares: "", land_use_type: "natural_forest", year_established: "",
  restoration_area_hectares: "", certified_sustainable: false,
};

function RegisterSiteForm({ entityId, onDone, onError }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: k === "country" ? String(val).toUpperCase().slice(0, 2) : val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    onError(null);
    try {
      await apiFetch("/api/esrs/e4/sites", {
        method: "POST",
        body: {
          entity_id: entityId,
          site_id: form.site_id.trim(),
          site_name: form.site_name.trim(),
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          country: form.country.trim().toUpperCase(),
          area_hectares: Number(form.area_hectares),
          land_use_type: form.land_use_type,
          year_established: form.year_established === "" ? null : Number(form.year_established),
          restoration_area_hectares:
            form.restoration_area_hectares === "" ? 0 : Number(form.restoration_area_hectares),
          certified_sustainable: form.certified_sustainable,
        },
      });
      setForm(EMPTY_FORM);
      setOpen(false);
      await onDone();
    } catch (err) {
      onError(err.message || "Failed to register site");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
      >
        <Overline Icon={Plus} style={{ marginBottom: 0 }}>Register site</Overline>
        {open ? <ChevronDown size={16} strokeWidth={1.5} color={C.body} />
              : <ChevronRight size={16} strokeWidth={1.5} color={C.body} />}
      </div>

      {open && (
        <form onSubmit={submit} style={{ marginTop: 16, ...gap(14) }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            <div>
              <Label>Site ID *</Label>
              <input style={field} value={form.site_id} onChange={set("site_id")} required placeholder="site-001" />
            </div>
            <div>
              <Label>Site name *</Label>
              <input style={field} value={form.site_name} onChange={set("site_name")} required placeholder="North Estate" />
            </div>
            <div>
              <Label>Country (ISO2) *</Label>
              <input style={field} value={form.country} onChange={set("country")} required maxLength={2} placeholder="DE" />
            </div>
            <div>
              <Label>Latitude * (−90…90)</Label>
              <input style={field} type="number" step="any" min={-90} max={90} value={form.latitude} onChange={set("latitude")} required placeholder="51.16" />
            </div>
            <div>
              <Label>Longitude * (−180…180)</Label>
              <input style={field} type="number" step="any" min={-180} max={180} value={form.longitude} onChange={set("longitude")} required placeholder="10.45" />
            </div>
            <div>
              <Label>Area (hectares) *</Label>
              <input style={field} type="number" step="any" min={0} value={form.area_hectares} onChange={set("area_hectares")} required placeholder="120" />
            </div>
            <div>
              <Label>Land-use type *</Label>
              <select style={field} value={form.land_use_type} onChange={set("land_use_type")} required>
                {LAND_USE.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <Label>Year established</Label>
              <input style={field} type="number" value={form.year_established} onChange={set("year_established")} placeholder="2015" />
            </div>
            <div>
              <Label>Restoration area (ha)</Label>
              <input style={field} type="number" step="any" min={0} value={form.restoration_area_hectares} onChange={set("restoration_area_hectares")} placeholder="0" />
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13, color: C.body, cursor: "pointer" }}>
            <input type="checkbox" checked={form.certified_sustainable} onChange={set("certified_sustainable")} style={{ accentColor: C.primary, width: 15, height: 15 }} />
            Certified sustainable
          </label>

          <div style={{ display: "flex", gap: 8 }}>
            <Btn type="submit" Icon={Plus} disabled={busy || !entityId}>
              {busy ? "Registering…" : "Register site"}
            </Btn>
            <Btn variant="ghost" onClick={() => { setForm(EMPTY_FORM); setOpen(false); }}>Cancel</Btn>
          </div>
        </form>
      )}
    </Card>
  );
}

// ── Per-site inline assessment + land-use change ────────────────────────────────
function SiteActions({ entityId, site, onError, onChanged }) {
  const [assessment, setAssessment] = useState(site.latest_assessment || null);
  const [busy, setBusy] = useState("");
  const [luOpen, setLuOpen] = useState(false);
  const [lu, setLu] = useState({ land_use_type: site.site_data?.land_use_type || "natural_forest", previous_land_use: "" });

  const runAssess = async () => {
    setBusy("assess");
    onError(null);
    try {
      const r = await apiFetch(
        `/api/esrs/e4/sites/${encodeURIComponent(site.site_data.site_id)}/assess?entity_id=${encodeURIComponent(entityId)}`,
        { method: "POST" }
      );
      setAssessment(r.assessment);
    } catch (err) {
      onError(err.message || "Assessment failed");
    } finally {
      setBusy("");
    }
  };

  const submitLandUse = async (e) => {
    e.preventDefault();
    setBusy("land-use");
    onError(null);
    try {
      const r = await apiFetch(
        `/api/esrs/e4/sites/${encodeURIComponent(site.site_data.site_id)}/land-use`,
        {
          method: "POST",
          body: {
            entity_id: entityId,
            land_use_type: lu.land_use_type,
            previous_land_use: lu.previous_land_use || null,
          },
        }
      );
      setAssessment(r.assessment);
      setLuOpen(false);
      await onChanged();
    } catch (err) {
      onError(err.message || "Land-use update failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div style={{ ...gap(12) }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="secondary" Icon={ScanLine} onClick={runAssess} disabled={!!busy || !entityId}>
          {busy === "assess" ? "Assessing…" : "Assess"}
        </Btn>
        <Btn variant="secondary" Icon={Layers} onClick={() => setLuOpen((o) => !o)} disabled={!!busy}>
          Land-use change
        </Btn>
      </div>

      {assessment && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
          {severityBadge(assessment.severity)}
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, color: C.body }}>Protected-area proximity</span>
            <Data color={assessment.protected_area_risk?.is_sensitive ? C.accent : C.body}>
              {assessment.protected_area_risk?.proximity_status ?? "—"}
            </Data>
          </span>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, color: C.body }}>MSA factor</span>
            <Data>{fmt(assessment.msa_impact?.msa_factor, 3)}</Data>
            {MSA_SOURCE[assessment.land_use_impact?.msa_factor_source] && (
              <DataQualityBadge {...MSA_SOURCE[assessment.land_use_impact.msa_factor_source]} />
            )}
          </span>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 11, color: C.body }}>Risk score</span>
            <Data>{fmt(assessment.risk_score, 1)}</Data>
          </span>
        </div>
      )}

      {luOpen && (
        <form onSubmit={submitLandUse} style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
          <div>
            <Label>Current land-use</Label>
            <select style={{ ...field, minWidth: 170 }} value={lu.land_use_type}
              onChange={(e) => setLu((s) => ({ ...s, land_use_type: e.target.value }))}>
              {LAND_USE.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <Label>Previous land-use</Label>
            <select style={{ ...field, minWidth: 170 }} value={lu.previous_land_use}
              onChange={(e) => setLu((s) => ({ ...s, previous_land_use: e.target.value }))}>
              <option value="">— none —</option>
              {LAND_USE.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <Btn type="submit" Icon={Layers} disabled={busy === "land-use"}>
            {busy === "land-use" ? "Recording…" : "Record"}
          </Btn>
        </form>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────
export default function E4Biodiversity() {
  const { entities, entityId, setEntityId } = useEntities("esrs_e4_entity_id");

  const [sites, setSites] = useState([]);
  const [footprint, setFootprint] = useState(null);
  const [e45, setE45] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      const q = `entity_id=${encodeURIComponent(entityId)}`;
      const [s, f, d] = await Promise.all([
        apiFetch(`/api/esrs/e4/sites?${q}`),
        apiFetch(`/api/esrs/e4/footprint?${q}`),
        apiFetch(`/api/esrs/e4/disclosure/e4-5?${q}`),
      ]);
      setSites(s?.sites || []);
      setFootprint(f || null);
      setE45(d || null);
    } catch (err) {
      setError(err.message || "Failed to load ESRS E4 data");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const restorationArea = useMemo(
    () => sites.reduce((sum, r) => sum + Number(r?.site_data?.restoration_area_hectares || 0), 0),
    [sites]
  );

  const summary = footprint?.portfolio_summary;

  // DataTable keys off row[keyField]; expose a stable key from the nested site_id.
  const siteRows = useMemo(
    () => sites.map((r) => ({ ...r, __k: r.site_data?.site_id })),
    [sites]
  );

  const columns = [
    { key: "site_id", label: "Site ID", render: (r) => <Data color={C.ink}>{r.site_data?.site_id ?? "—"}</Data> },
    { key: "site_name", label: "Name", render: (r) => (
        <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13, color: C.ink }}>{r.site_data?.site_name ?? "—"}</span>
      ) },
    { key: "country", label: "Country", render: (r) => <Data color={C.body}>{r.site_data?.country ?? "—"}</Data> },
    { key: "area", label: "Area (ha)", align: "right", render: (r) => <Data>{fmt(r.site_data?.area_hectares)}</Data> },
    { key: "land_use", label: "Land-use", render: (r) => (
        <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13, color: C.body }}>{landUseLabel(r.site_data?.land_use_type)}</span>
      ) },
    { key: "severity", label: "Severity", render: (r) => severityBadge(r.latest_assessment?.severity) },
    { key: "actions", label: "", align: "right", render: (r) => {
        const id = r.site_data?.site_id;
        const isOpen = expanded === id;
        return (
          <Btn variant="ghost" Icon={isOpen ? ChevronDown : ChevronRight}
            onClick={() => setExpanded(isOpen ? null : id)}>
            {isOpen ? "Hide" : "Actions"}
          </Btn>
        );
      } },
  ];

  return (
    <Page>
      <PageHeader
        overline="ESRS E4 · Biodiversity & Ecosystems"
        OverlineIcon={TreePine}
        title="Biodiversity & Ecosystems"
        subtitle="ESRS E4-5 site impact metrics — operational footprint by land-use type, proximity to biodiversity-sensitive protected areas, and GLOBIO Mean Species Abundance (MSA) factors driving each site's impact severity."
        entities={entities}
        entityId={entityId}
        onEntityChange={setEntityId}
        onRefresh={load}
        loading={loading}
      />

      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {!entityId ? (
        <EmptyState
          Icon={TreePine}
          title="Select a reporting entity"
          body="Choose an entity to review its registered sites, biodiversity footprint and ESRS E4-5 impact metrics."
        />
      ) : loading && !footprint ? (
        <div style={{ ...gap(16) }}>
          <Skeleton h={96} />
          <Skeleton h={220} />
        </div>
      ) : (
        <div style={{ ...gap(16) }}>
          {/* KPI strip */}
          <Card>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 20 }}>
              <Metric label="Total sites" value={fmt(summary?.total_sites, 0)} />
              <Metric label="Total area" value={fmt(summary?.total_area_hectares)} unit="ha" />
              <Metric
                label="Sites near protected areas"
                value={fmt(summary?.sites_in_sensitive_areas, 0)}
                color={summary?.sites_in_sensitive_areas > 0 ? C.accent : C.ink}
              />
              <Metric label="Restoration area" value={fmt(restorationArea)} unit="ha" color={C.primary} />
              {e45 && (
                <Metric label="Portfolio MSA" value={fmt(e45.portfolio_msa, 3)} />
              )}
            </div>
          </Card>

          {/* Sites */}
          <Card>
            <Overline Icon={MapPin}>Sites</Overline>
            {sites.length === 0 ? (
              <EmptyState
                Icon={Sprout}
                title="No sites registered"
                body="Register an operational site below to run its biodiversity assessment and surface ESRS E4-5 impact metrics."
              />
            ) : (
              <>
                <DataTable
                  columns={columns}
                  rows={siteRows}
                  keyField="__k"
                  emptyLabel="No sites"
                />
                {sites.map((r) => {
                  const id = r.site_data?.site_id;
                  if (expanded !== id) return null;
                  return (
                    <div key={id} style={{ marginTop: 12, padding: 16, borderRadius: 6, background: C.bg, border: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <MapPin size={13} strokeWidth={1.5} color={C.muted} />
                        <Data color={C.ink}>{id}</Data>
                        <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 12, color: C.body }}>
                          {r.site_data?.site_name}
                        </span>
                      </div>
                      <SiteActions
                        entityId={entityId}
                        site={r}
                        onError={setError}
                        onChanged={load}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </Card>

          {/* Register site */}
          <RegisterSiteForm entityId={entityId} onDone={load} onError={setError} />

          {/* E4-5 land-use change footnote */}
          {e45?.land_use_change && (
            <Card>
              <Overline Icon={Recycle}>ESRS E4-5 · Land-use change</Overline>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
                <Metric label="Hectares changed" value={fmt(e45.land_use_change.hectares_with_land_use_change)} unit="ha" />
                <Metric label="Sites degraded" value={fmt(e45.land_use_change.sites_with_degradation, 0)} color={C.accent} />
                <Metric label="Sites improved" value={fmt(e45.land_use_change.sites_with_improvement, 0)} color={C.primary} />
                <Metric label="MSA loss" value={fmt(e45.total_msa_loss_hectares)} unit="ha" />
              </div>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
